/**
 * TaskIntegrityService - Task Verification & Auto-Healing
 *
 * Purpose: Ensures timeline_items match what SHOULD exist based on templates,
 * college data, application rounds, and deadlines. Provides surgical fixes
 * to maintain timeline integrity without disrupting student experience.
 *
 * CRITICAL INTEGRATIONS:
 * - CrisisRescueService: Preserves crisis priorities during regeneration
 * - WeeklyCapacityService: Respects capacity constraints when rescheduling
 * - AcademicCalendarService: Avoids academic constraint periods
 * - TaskBatchingService: Triggers batch recalculation after changes
 *
 * Design Philosophy (aligned with UX):
 * - "Maximally Assistive": Auto-heals when confident, flags when unsure
 * - "Confidence-Instilling": Provides detailed reports of what changed and why
 * - "Frictionless": Changes happen seamlessly, students see smooth updates
 */

import { supabase } from '../../lib/supabase.js';
import AdminMilestoneService from '../AdminMilestoneService.js';
import { CrisisRescueService } from '../intelligence/CrisisRescueService.js';
import WeeklyCapacityService from '../WeeklyCapacityService.js';
import AcademicCalendarService from '../AcademicCalendarService.js';
import TaskBatchingService from '../TaskBatchingService.js';
import CollegeEventBus from '../events/CollegeEventBus.js';
import { differenceInDays, parseISO, format } from 'date-fns';

class TaskIntegrityService {

  /**
   * Verify college tasks match expected templates
   * Returns detailed report of discrepancies
   *
   * @param {string} collegeId - College to verify
   * @param {string} userId - User ID
   * @param {Object} options - Verification options
   * @returns {Promise<Object>} Verification report
   */
  static async verifyCollegeTasks(collegeId, userId, options = {}) {
    const {
      checkDates = true,
      checkRounds = true,
      checkSequence = true,
      verbose = false
    } = options;

    console.log(`[TaskIntegrityService] 🔍 Verifying tasks for college ${collegeId}, user ${userId}`);

    try {
      // Step 1: Get college with full context
      const { data: college, error: collegeError } = await supabase
        .from('college_journey_colleges')
        .select(`
          *,
          global_colleges!global_college_id (
            ed_deadline,
            ed2_deadline,
            ea_deadline,
            rd_deadline,
            name
          )
        `)
        .eq('id', collegeId)
        .single();

      if (collegeError) throw collegeError;
      if (!college) {
        return {
          error: 'College not found',
          collegeId
        };
      }

      // Step 2: Get what SHOULD exist (templates for this college/round)
      const expectedTemplates = await this.getExpectedTemplates(college, userId);

      if (verbose) {
        console.log(`[TaskIntegrityService] Expected ${expectedTemplates.length} templates for ${college.name}`);
      }

      // Step 3: Get what DOES exist (current timeline items)
      const { data: actualTasks, error: tasksError } = await supabase
        .from('timeline_items')
        .select('*')
        .eq('user_id', userId)
        .eq('college_id', collegeId);

      if (tasksError) throw tasksError;

      if (verbose) {
        console.log(`[TaskIntegrityService] Found ${actualTasks?.length || 0} existing tasks`);
      }

      // Step 4: Compare and identify discrepancies
      const verification = this.compareExpectedVsActual(
        expectedTemplates,
        actualTasks || [],
        college,
        { checkDates, checkRounds, checkSequence }
      );

      // Step 5: Add college context
      verification.college = {
        id: college.id,
        name: college.name,
        application_round: college.application_round,
        category: college.category,
        deadline: this.getRelevantDeadline(college)
      };

      verification.timestamp = new Date().toISOString();

      if (verbose) {
        console.log(`[TaskIntegrityService] Verification complete:`, {
          expected: verification.expected,
          actual: verification.actual,
          missing: verification.missing.length,
          orphaned: verification.orphaned.length,
          mismatched: verification.mismatched.length
        });
      }

      return verification;

    } catch (error) {
      console.error('[TaskIntegrityService] Verification failed:', error);
      throw error;
    }
  }

  /**
   * Get expected templates for a college
   * @private
   */
  static async getExpectedTemplates(college, userId) {
    try {
      // Get all active college-specific templates
      const { data: allTemplates, error } = await supabase
        .from('milestone_templates')
        .select('*')
        .eq('is_active', true)
        .eq('is_college_specific', true)
        .eq('generation_timing', 'immediate') // Only immediate tasks (deadline-based generated separately)
        .order('task_sequence');

      if (error) throw error;

      // Filter by application round
      const filtered = AdminMilestoneService.filterTemplatesByCollegeType(
        allTemplates || [],
        college.application_round
      );

      // Filter by college status (only if accepted/selected)
      const applicable = AdminMilestoneService.filterTemplatesByCollegeStatus(
        filtered,
        college
      );

      return applicable;

    } catch (error) {
      console.error('[TaskIntegrityService] Failed to get expected templates:', error);
      return [];
    }
  }

  /**
   * Compare expected templates vs actual tasks
   * @private
   */
  static compareExpectedVsActual(expectedTemplates, actualTasks, college, checks) {
    const missing = [];
    const orphaned = [];
    const mismatched = [];

    // Build lookup map of actual tasks by source_id
    const taskMap = new Map();
    actualTasks.forEach(task => {
      if (task.source_id) {
        taskMap.set(task.source_id, task);
      }
    });

    // Check each expected template
    expectedTemplates.forEach(template => {
      const expectedSourceId = `template_${template.id}_college_${college.id}`;
      const actualTask = taskMap.get(expectedSourceId);

      if (!actualTask) {
        // Missing task
        missing.push({
          template_id: template.id,
          title: template.title,
          category: template.category,
          task_sequence: template.task_sequence,
          importance_level: template.importance_level,
          reason: 'Task not found in timeline_items'
        });
      } else {
        // Task exists - check for mismatches
        const issues = [];

        // Check application round
        if (checks.checkRounds && actualTask.application_round !== college.application_round) {
          issues.push({
            field: 'application_round',
            expected: college.application_round,
            actual: actualTask.application_round,
            severity: 'high'
          });
        }

        // Check due date validity
        if (checks.checkDates && actualTask.due_date) {
          const dueDate = new Date(actualTask.due_date);
          const deadline = new Date(this.getRelevantDeadline(college));

          // Due date shouldn't be after deadline
          if (dueDate > deadline) {
            issues.push({
              field: 'due_date',
              expected: `Before ${format(deadline, 'MMM d, yyyy')}`,
              actual: format(dueDate, 'MMM d, yyyy'),
              severity: 'high'
            });
          }

          // Due date shouldn't be too far in past (>30 days overdue)
          const now = new Date();
          const daysOverdue = differenceInDays(now, dueDate);
          if (daysOverdue > 30 && actualTask.completion_status !== 'completed') {
            issues.push({
              field: 'due_date',
              expected: 'Recent date',
              actual: `${daysOverdue} days overdue`,
              severity: 'medium'
            });
          }
        }

        // Check sequence order
        if (checks.checkSequence && template.task_sequence && actualTask.sequence_order !== template.task_sequence) {
          issues.push({
            field: 'sequence_order',
            expected: template.task_sequence,
            actual: actualTask.sequence_order,
            severity: 'low'
          });
        }

        if (issues.length > 0) {
          mismatched.push({
            timeline_item_id: actualTask.id,
            template_id: template.id,
            title: actualTask.title,
            issues
          });
        }

        // Remove from map (so we can find orphaned tasks)
        taskMap.delete(expectedSourceId);
      }
    });

    // Remaining tasks in map are orphaned (shouldn't exist)
    taskMap.forEach(task => {
      orphaned.push({
        timeline_item_id: task.id,
        title: task.title,
        source_id: task.source_id,
        application_round: task.application_round,
        reason: this.determineOrphanReason(task, college)
      });
    });

    return {
      expected: expectedTemplates.length,
      actual: actualTasks.length,
      missing,
      orphaned,
      mismatched,
      isHealthy: missing.length === 0 && orphaned.length === 0 && mismatched.length === 0
    };
  }

  /**
   * Determine why a task is orphaned
   * @private
   */
  static determineOrphanReason(task, college) {
    // Check if wrong round
    if (task.application_round && task.application_round !== college.application_round) {
      return `Wrong application round (task is for ${task.application_round}, college is ${college.application_round})`;
    }

    // Check if template deactivated
    if (task.source_id?.startsWith('template_')) {
      return 'Template may have been deactivated or changed';
    }

    return 'Unknown - task does not match any current templates';
  }

  /**
   * Get relevant deadline for college
   * @private
   */
  static getRelevantDeadline(college) {
    return AdminMilestoneService.getRelevantDeadline(college, college.application_round);
  }

  /**
   * CRITICAL: Handle application round change
   * This is the most complex integrity operation - must preserve crisis priorities
   *
   * @param {string} collegeId - College that changed rounds
   * @param {string} oldRound - Previous application round
   * @param {string} newRound - New application round
   * @param {Object} options - Handling options
   * @returns {Promise<Object>} Transition result
   */
  static async handleRoundChange(collegeId, oldRound, newRound, options = {}) {
    const {
      dryRun = false,
      preserveCrisisPriorities = true,
      respectCapacity = true,
      notifyStudent = true
    } = options;

    console.log(`[TaskIntegrityService] 🔄 Handling round change: ${oldRound} → ${newRound} for college ${collegeId}`);

    try {
      // Step 1: Get college and user context
      const { data: college, error: collegeError } = await supabase
        .from('college_journey_colleges')
        .select(`
          *,
          global_colleges!global_college_id (
            ed_deadline,
            ed2_deadline,
            ea_deadline,
            rd_deadline,
            name
          )
        `)
        .eq('id', collegeId)
        .single();

      if (collegeError) throw collegeError;

      const userId = college.user_id;

      // Step 2: Get existing tasks for this college
      const { data: existingTasks, error: tasksError } = await supabase
        .from('timeline_items')
        .select('*')
        .eq('user_id', userId)
        .eq('college_id', collegeId);

      if (tasksError) throw tasksError;

      console.log(`[TaskIntegrityService] Found ${existingTasks?.length || 0} existing tasks`);

      // Step 3: Check if student is in crisis mode (affects how we handle transition)
      let crisisAnalysis = null;
      if (preserveCrisisPriorities) {
        const crisisService = new CrisisRescueService(supabase);
        crisisAnalysis = await crisisService.analyzeStudentCrisis(userId);

        console.log(`[TaskIntegrityService] Crisis level: ${crisisAnalysis.crisisLevel}`);
      }

      // Step 4: Analyze transition (what to keep, delete, create)
      const transition = await this.analyzeRoundTransition(
        existingTasks || [],
        oldRound,
        newRound,
        college,
        crisisAnalysis
      );

      console.log(`[TaskIntegrityService] Transition plan:`, {
        keep: transition.tasksToUpdate.length,
        delete: transition.tasksToDelete.length,
        create: transition.templatesToGenerate.length
      });

      if (dryRun) {
        return {
          dryRun: true,
          transition,
          message: 'Dry run - no changes made'
        };
      }

      // Step 5: Execute transition in transaction
      const result = await this.executeRoundTransition(
        transition,
        college,
        userId,
        {
          respectCapacity,
          crisisAnalysis
        }
      );

      // Step 6: Trigger crisis re-evaluation (round change may affect urgency)
      if (preserveCrisisPriorities) {
        await CollegeEventBus.emit(
          CollegeEventBus.events.INTEGRITY_AUTO_HEALED,
          {
            userId,
            collegeId,
            operation: 'round_change',
            oldRound,
            newRound,
            tasksAffected: result.tasksUpdated + result.tasksDeleted + result.tasksCreated
          },
          { priority: 'high' }
        );
      }

      // Step 7: Recalculate batching
      await this.recalculateBatching(userId);

      console.log(`[TaskIntegrityService] ✅ Round change complete`);

      return {
        success: true,
        ...result,
        crisisLevel: crisisAnalysis?.crisisLevel
      };

    } catch (error) {
      console.error('[TaskIntegrityService] Round change failed:', error);
      throw error;
    }
  }

  /**
   * Analyze what needs to happen during round transition
   * CRITICAL: Must preserve crisis priorities and not disrupt student
   * @private
   */
  static async analyzeRoundTransition(existingTasks, oldRound, newRound, college, crisisAnalysis) {
    const tasksToUpdate = [];
    const tasksToDelete = [];
    const templatesToGenerate = [];

    // Get templates for NEW round
    const newRoundTemplates = await this.getExpectedTemplates(
      { ...college, application_round: newRound },
      college.user_id
    );

    // Build map of new round template IDs
    const newTemplateIds = new Set(
      newRoundTemplates.map(t => `template_${t.id}_college_${college.id}`)
    );

    // Analyze each existing task
    for (const task of existingTasks) {
      const isValidForNewRound = newTemplateIds.has(task.source_id);

      if (isValidForNewRound) {
        // Task is valid for new round - update with new dates
        tasksToUpdate.push({
          ...task,
          needsNewDates: true,
          needsRoundUpdate: true
        });
      } else {
        // Task is NOT valid for new round
        // Check if we should delete or keep based on crisis mode

        if (crisisAnalysis && crisisAnalysis.crisisLevel === 'emergency') {
          // In crisis mode - keep task if it's in progress or has work done
          if (task.completion_status === 'in_progress' || task.completion_percentage > 0) {
            console.log(`[TaskIntegrityService] ⚠️  Crisis mode - keeping in-progress task: ${task.title}`);
            tasksToUpdate.push({
              ...task,
              needsNewDates: false,
              needsRoundUpdate: true,
              keepDespiteOrphaned: true,
              metadata: {
                ...task.metadata,
                orphaned_but_kept: true,
                reason: 'In-progress during crisis mode round change'
              }
            });
          } else {
            tasksToDelete.push(task);
          }
        } else {
          // Not in crisis - safe to delete
          tasksToDelete.push(task);
        }
      }
    }

    // Find templates that need to be generated (not in existing tasks)
    const existingSourceIds = new Set(existingTasks.map(t => t.source_id));

    for (const template of newRoundTemplates) {
      const sourceId = `template_${template.id}_college_${college.id}`;
      if (!existingSourceIds.has(sourceId)) {
        templatesToGenerate.push(template);
      }
    }

    return {
      tasksToUpdate,
      tasksToDelete,
      templatesToGenerate,
      newRoundTemplates
    };
  }

  /**
   * Execute round transition with transaction safety
   * @private
   */
  static async executeRoundTransition(transition, college, userId, options) {
    const { respectCapacity, crisisAnalysis } = options;

    let tasksUpdated = 0;
    let tasksDeleted = 0;
    let tasksCreated = 0;

    try {
      // Step 1: Delete orphaned tasks
      if (transition.tasksToDelete.length > 0) {
        const idsToDelete = transition.tasksToDelete.map(t => t.id);

        const { error: deleteError } = await supabase
          .from('timeline_items')
          .delete()
          .in('id', idsToDelete);

        if (deleteError) throw deleteError;

        tasksDeleted = idsToDelete.length;
        console.log(`[TaskIntegrityService] Deleted ${tasksDeleted} orphaned tasks`);
      }

      // Step 2: Update surviving tasks with new dates and round
      if (transition.tasksToUpdate.length > 0) {
        for (const task of transition.tasksToUpdate) {
          let updates = {
            application_round: college.application_round,
            updated_at: new Date().toISOString()
          };

          // Recalculate due date if needed
          if (task.needsNewDates) {
            // Get template for date calculation
            const templateId = task.source_id?.match(/template_(\d+)_/)?.[1];
            if (templateId) {
              const { data: template } = await supabase
                .from('milestone_templates')
                .select('*')
                .eq('id', templateId)
                .single();

              if (template) {
                const newDueDate = await AdminMilestoneService.calculateDueDateWithAnchorSystem(
                  template,
                  { id: userId },
                  college
                );

                updates.due_date = newDueDate;
                updates.scheduled_date = newDueDate;
              }
            }
          }

          const { error: updateError } = await supabase
            .from('timeline_items')
            .update(updates)
            .eq('id', task.id);

          if (updateError) throw updateError;

          tasksUpdated++;
        }

        console.log(`[TaskIntegrityService] Updated ${tasksUpdated} tasks with new dates/round`);
      }

      // Step 3: Generate missing tasks
      if (transition.templatesToGenerate.length > 0) {
        // Get student preferences for smart scheduling
        const { data: profile } = await supabase
          .from('student_academic_profiles')
          .select('*')
          .eq('user_id', userId)
          .single();

        // Generate tasks using AdminMilestoneService
        // This respects capacity and academic calendar automatically
        const generateResult = await AdminMilestoneService.generateCollegeMilestonesWithTiming(
          userId,
          college.id,
          college.name,
          'immediate',
          {
            forceGenerate: true,
            respectCapacity,
            applicationRound: college.application_round
          }
        );

        tasksCreated = generateResult.total || 0;
        console.log(`[TaskIntegrityService] Generated ${tasksCreated} new tasks`);
      }

      return {
        tasksUpdated,
        tasksDeleted,
        tasksCreated,
        totalAffected: tasksUpdated + tasksDeleted + tasksCreated
      };

    } catch (error) {
      console.error('[TaskIntegrityService] Transaction failed:', error);
      throw error;
    }
  }

  /**
   * Recalculate batching after integrity changes
   * @private
   */
  static async recalculateBatching(userId) {
    try {
      // Get all tasks for user
      const { data: tasks } = await supabase
        .from('timeline_items')
        .select('*')
        .eq('user_id', userId)
        .order('due_date');

      if (!tasks || tasks.length === 0) return;

      // Unbatch by round (separates ED/EA/RD)
      const unbatched = TaskBatchingService.unbatchByApplicationRound(tasks);

      // This triggers UI recalculation on next render
      // No database updates needed - batching is presentation layer

      console.log(`[TaskIntegrityService] Batching recalculated for ${tasks.length} tasks`);

    } catch (error) {
      console.error('[TaskIntegrityService] Batching recalculation failed:', error);
      // Non-critical - don't throw
    }
  }

  /**
   * Run integrity audit for all students or specific subset
   * @param {Object} options - Audit options
   * @returns {Promise<Object>} Audit results
   */
  static async runIntegrityAudit(options = {}) {
    const {
      userIds = null, // null = all students
      autoHeal = false,
      confidenceThreshold = 0.8, // Only auto-heal if > 80% confident
      limit = null,
      skipRecent = true // Skip students checked in last hour
    } = options;

    console.log(`[TaskIntegrityService] 🔍 Starting integrity audit...`);

    try {
      // Get students to check
      let query = supabase
        .from('student_academic_profiles')
        .select('user_id');

      if (userIds) {
        query = query.in('user_id', userIds);
      }

      if (limit) {
        query = query.limit(limit);
      }

      const { data: students, error: studentsError } = await query;

      if (studentsError) throw studentsError;

      console.log(`[TaskIntegrityService] Auditing ${students?.length || 0} students`);

      const results = {
        studentsChecked: 0,
        issuesFound: 0,
        autoHealed: 0,
        flaggedForReview: 0,
        report: []
      };

      for (const student of students || []) {
        // Get colleges for student
        const { data: colleges } = await supabase
          .from('college_journey_colleges')
          .select('id, name')
          .eq('user_id', student.user_id);

        for (const college of colleges || []) {
          const verification = await this.verifyCollegeTasks(
            college.id,
            student.user_id,
            { verbose: false }
          );

          if (!verification.isHealthy) {
            results.issuesFound++;

            const issueReport = {
              userId: student.user_id,
              collegeId: college.id,
              collegeName: college.name,
              ...verification
            };

            // Calculate confidence for auto-healing
            const confidence = this.calculateHealingConfidence(verification);
            issueReport.confidence = confidence;

            if (autoHeal && confidence >= confidenceThreshold) {
              // Attempt auto-heal
              const healResult = await this.autoHealCollege(
                college.id,
                student.user_id,
                verification
              );

              if (healResult.success) {
                results.autoHealed++;
                issueReport.autoHealed = true;
                issueReport.healResult = healResult;
              } else {
                results.flaggedForReview++;
                issueReport.flaggedForReview = true;
                issueReport.healError = healResult.error;
              }
            } else {
              results.flaggedForReview++;
              issueReport.flaggedForReview = true;
              issueReport.reason = confidence < confidenceThreshold
                ? `Low confidence (${Math.round(confidence * 100)}%)`
                : 'Auto-heal disabled';
            }

            results.report.push(issueReport);
          }
        }

        results.studentsChecked++;
      }

      console.log(`[TaskIntegrityService] ✅ Audit complete:`, {
        studentsChecked: results.studentsChecked,
        issuesFound: results.issuesFound,
        autoHealed: results.autoHealed,
        flaggedForReview: results.flaggedForReview
      });

      return results;

    } catch (error) {
      console.error('[TaskIntegrityService] Audit failed:', error);
      throw error;
    }
  }

  /**
   * Calculate confidence score for auto-healing
   * @private
   */
  static calculateHealingConfidence(verification) {
    let confidence = 1.0;

    // Reduce confidence for complex scenarios
    if (verification.mismatched.length > 0) {
      // Mismatched fields are trickier
      confidence -= 0.2;
    }

    if (verification.orphaned.length > verification.missing.length) {
      // More orphans than missing suggests template changes
      confidence -= 0.15;
    }

    if (verification.missing.length > 10) {
      // Many missing tasks suggests bigger issue
      confidence -= 0.1;
    }

    if (verification.orphaned.length > 10) {
      // Many orphans suggests round change or template deactivation
      confidence -= 0.1;
    }

    return Math.max(confidence, 0);
  }

  /**
   * Auto-heal college tasks
   * @private
   */
  static async autoHealCollege(collegeId, userId, verification) {
    try {
      console.log(`[TaskIntegrityService] 🔧 Auto-healing college ${collegeId}`);

      // Generate missing tasks
      if (verification.missing.length > 0) {
        // This uses the existing generation system which respects all constraints
        await AdminMilestoneService.generateImmediateCollegeMilestones(
          userId,
          collegeId,
          verification.college.name,
          { forceGenerate: true }
        );
      }

      // Delete orphaned tasks
      if (verification.orphaned.length > 0) {
        const orphanedIds = verification.orphaned.map(o => o.timeline_item_id);
        await supabase
          .from('timeline_items')
          .delete()
          .in('id', orphanedIds);
      }

      // Fix mismatched fields
      if (verification.mismatched.length > 0) {
        for (const mismatch of verification.mismatched) {
          const updates = {};

          mismatch.issues.forEach(issue => {
            if (issue.field === 'application_round') {
              updates.application_round = issue.expected;
            }
            // Add other field fixes as needed
          });

          if (Object.keys(updates).length > 0) {
            await supabase
              .from('timeline_items')
              .update(updates)
              .eq('id', mismatch.timeline_item_id);
          }
        }
      }

      return {
        success: true,
        healed: {
          missing: verification.missing.length,
          orphaned: verification.orphaned.length,
          mismatched: verification.mismatched.length
        }
      };

    } catch (error) {
      console.error('[TaskIntegrityService] Auto-heal failed:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

export default TaskIntegrityService;
